import React from 'react';
import { RefreshCw } from 'lucide-react';

interface PdfDownloadShellProps {
  status: 'loading' | 'generating' | 'success' | 'error' | 'unavailable';
  errorMessage?: string;
  fileType?: 'PDF' | 'Spreadsheet';
}

const PdfDownloadShell: React.FC<PdfDownloadShellProps> = ({ status, errorMessage, fileType = 'PDF' }) => {
  return (
    <div className="min-h-screen bg-rs-off-black flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-rs-jet rounded-lg shadow-xl p-8 border border-rs-green border-opacity-30">
        {status === 'loading' && (
          <div className="text-center">
            <RefreshCw className="w-12 h-12 text-rs-green animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-rs-off-white mb-2">Checking for PDF...</h2>
            <p className="text-rs-light-gray">Please wait</p>
          </div>
        )}

        {status === 'generating' && (
          <div className="text-center">
            <RefreshCw className="w-12 h-12 text-rs-green animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-rs-off-white mb-2">Generating {fileType}...</h2>
            <p className="text-rs-light-gray">This may take a moment</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="w-12 h-12 bg-rs-green rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-rs-off-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-rs-off-white mb-2">Download Started</h2>
            <p className="text-rs-light-gray">Your {fileType} should download automatically</p>
          </div>
        )}

        {status === 'unavailable' && (
          <div className="text-center">
            <div className="w-12 h-12 bg-rs-jet-light rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-rs-off-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-rs-off-white mb-2">Report Not Yet Available</h2>
            <p className="text-rs-light-gray">This report has not been published yet. Please check back later.</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-rs-off-white mb-2">Error</h2>
            <p className="text-red-400">{errorMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfDownloadShell;
